/** 가입 승인 관리(최고관리자) · 직원 관리(회사관리자) 창 */
import React, { useEffect, useState } from 'react';
import { overlay, box, head, xBtn, inp, btn, smBtn, chip } from './ui';
import { CompanyRow, CompStatus, listCompanies, companyAction, StaffRow, listStaff, addStaff, staffAction, errMsg, fetchBizFile, resendNotify, InquiryRow, listInquiries, replyInquiry } from '../utils/api';

const sm = (bg: string): React.CSSProperties => ({ ...smBtn(bg), marginLeft: 4 });
const fmt = (s: string | null) => (s || '').replace('T', ' ').slice(0, 16);
const bizFmt = (s: string | null) => (s && s.length === 10 ? `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}` : s || '');

const ST: Record<CompStatus, { label: string; color: string }> = {
  PENDING: { label: '승인 대기', color: '#F76707' }, APPROVED: { label: '승인', color: '#03B26C' },
  REJECTED: { label: '반려', color: '#F04452' }, SUSPENDED: { label: '정지', color: '#8B95A1' },
};

export const CompanyAdminDialog: React.FC<{ onClose: () => void; onChanged?: () => void }> = ({ onClose, onChanged }) => {
  const [tab, setTab] = useState<CompStatus | ''>('PENDING');
  const [rows, setRows] = useState<CompanyRow[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = (t = tab) => listCompanies(t).then(r => { setRows(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { setRows(null); load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (c: CompanyRow, action: 'approve' | 'reject' | 'suspend' | 'resume') => {
    let reason: string | undefined;
    if (action === 'reject') {
      const r = window.prompt(`${c.COMP_NM} 가입을 반려합니다. 사유를 입력하세요 (신청자가 로그인할 때 보입니다)`);
      if (!r?.trim()) return;
      reason = r.trim();
    } else if (action === 'suspend' && !window.confirm(`${c.COMP_NM}의 이용을 정지할까요? 이 회사 계정은 바로 쓸 수 없게 됩니다.`)) return;
    try { await companyAction(c.COMP_ID, action, reason); load(); onChanged?.(); } catch (e) { setErr(errMsg(e)); }
  };
  const viewFile = async (c: CompanyRow) => {
    const w = window.open('', '_blank'); // 팝업 차단 피하려고 클릭 즉시 창부터 엶
    try {
      const r = await fetchBizFile(c.COMP_ID);
      const url = URL.createObjectURL(r.data);
      if (w) w.location.href = url; else window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (e) { w?.close(); setErr('사업자등록증을 열 수 없습니다'); }
  };
  const td: React.CSSProperties = { padding: '10px 8px', borderBottom: '1px solid #F2F4F6', verticalAlign: 'top' };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 900 }}>
        <div style={head()}><b>가입 승인 관리</b><button onClick={onClose} style={xBtn}>✕</button></div>
        <div style={{ display: 'flex', gap: 6, padding: '4px 24px 12px', flexWrap: 'wrap' }}>
          {(['PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED', ''] as const).map(t => (
            <button key={t || 'all'} onClick={() => setTab(t)} style={chip(tab === t)}>{t ? ST[t].label : '전체'}</button>
          ))}
        </div>
        {err && <div style={{ color: '#F04452', padding: '0 24px 8px', fontSize: 13 }}>⚠ {err}</div>}
        <div style={{ overflow: 'auto', padding: '0 24px 20px' }}>
          {rows === null ? <div style={{ color: '#888', padding: 12 }}>불러오는 중…</div>
            : rows.length === 0 ? <div style={{ color: '#888', padding: 12 }}>{tab === 'PENDING' ? '대기 중인 신청이 없습니다' : '없습니다'}</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>
                  {['상태', '상호', '사업자번호', '대표자', '담당자', '신청일', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ddd' }}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.map(c => (
                  <React.Fragment key={c.COMP_ID}>
                    <tr onClick={() => setOpen(open === c.COMP_ID ? null : c.COMP_ID)} style={{ cursor: 'pointer' }}>
                      <td style={td}><span style={{ color: ST[c.STATUS].color, fontWeight: 'bold' }}>{ST[c.STATUS].label}</span></td>
                      <td style={td}><b>{c.COMP_NM}</b></td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{bizFmt(c.BIZ_NO)}</td>
                      <td style={td}>{c.CEO_NM}</td>
                      <td style={td}>{c.MGR_NM} {c.MGR_TEL || c.TEL}</td>
                      <td style={{ ...td, color: '#666' }}>{fmt(c.REG_DT)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        {c.STATUS === 'PENDING' && <><button style={sm('#03B26C')} onClick={() => act(c, 'approve')}>승인</button><button style={sm('#F04452')} onClick={() => act(c, 'reject')}>반려</button></>}
                        {c.STATUS === 'APPROVED' && <button style={sm('#616161')} onClick={() => act(c, 'suspend')}>정지</button>}
                        {c.STATUS === 'SUSPENDED' && <button style={sm('#03B26C')} onClick={() => act(c, 'resume')}>정지 해제</button>}
                        {c.STATUS === 'REJECTED' && <button style={sm('#03B26C')} onClick={() => act(c, 'approve')}>승인</button>}
                      </td>
                    </tr>
                    {open === c.COMP_ID && (
                      <tr><td colSpan={7} style={{ padding: '8px 12px 12px', background: '#fafcff', borderBottom: '1px solid #e3e8ee', fontSize: 13, lineHeight: 1.7 }}>
                        <b>관리자 아이디</b> {c.OWNER_ID} · <b>계정</b> {c.USER_CNT}개 · <b>도면</b> {c.DRAWING_CNT}건<br />
                        <b>회사 전화</b> {c.TEL || '-'} · <b>담당자 휴대폰</b> {c.MGR_TEL || '-'} · <b>이메일</b> {c.MGR_EMAIL || '-'}<br />
                        <b>주소</b> {c.ADDR || '-'}<br />
                        <b>사업자등록증</b> {c.BIZ_FILE_NM
                          ? <button onClick={() => viewFile(c)} style={{ ...sm('#3182F6'), marginLeft: 0 }}>📎 {c.BIZ_FILE_NM}</button>
                          : <span style={{ color: '#999' }}>없음</span>}<br />
                        {c.REQ_MEMO && <><b>요청 사항</b> {c.REQ_MEMO}<br /></>}
                        {c.REJECT_REASON && <><b>반려 사유</b> {c.REJECT_REASON}<br /></>}
                        {c.APPROVED_DT && <><b>승인일</b> {fmt(c.APPROVED_DT)}<br /></>}
                        <b>알림</b> 신청 → 관리자: {notiText(c.REQ_NOTI_YN, c.REQ_NOTI_MSG)}
                        {c.STATUS !== 'PENDING' && <> · 승인/반려 → 신청자: {notiText(c.APRV_NOTI_YN, c.APRV_NOTI_MSG)}
                          {(c.STATUS === 'APPROVED' || c.STATUS === 'REJECTED') && <button style={sm('#5C6BC0')} onClick={async () => { await resendNotify(c.COMP_ID, 'aprv'); load(); }}>다시 보내기</button>}</>}
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}</tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
};

const notiText = (yn: string | null, msg: string | null) => (
  <span style={{ color: yn === 'Y' ? '#03B26C' : yn === 'N' ? '#F04452' : '#999' }}>{yn === 'Y' ? '✓ ' : yn === 'N' ? '✗ ' : ''}{msg || '보낸 적 없음'}</span>
);

const ROLE: Record<string, string> = { SUPER: '최고관리자', OWNER: '회사관리자', STAFF: '직원', PROD: '생산 직원' };

export const StaffDialog: React.FC<{ me: string; compName: string; onClose: () => void }> = ({ me, compName, onClose }) => {
  const [rows, setRows] = useState<StaffRow[] | null>(null);
  const [f, setF] = useState({ id: '', name: '', pw: '', email: '', role: 'STAFF' as 'STAFF' | 'PROD' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = () => listStaff().then(r => setRows(r.data)).catch(e => setMsg({ ok: false, text: errMsg(e) }));
  useEffect(() => { load(); }, []);
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await addStaff(f.id.trim(), f.name.trim(), f.pw, f.email.trim() || undefined, f.role); setMsg({ ok: true, text: `${f.id} 추가했습니다 — 첫 로그인 때 비밀번호를 새로 정합니다` }); setF({ id: '', name: '', pw: '', email: '', role: f.role }); load(); }
    catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); }
  };
  const act = async (u: StaffRow, action: 'disable' | 'enable' | 'password') => {
    let pw: string | undefined;
    if (action === 'password') {
      const p = window.prompt(`${u.USER_NM}(${u.USER_ID})의 새 비밀번호 (8자 이상)`);
      if (!p) return;
      pw = p;
    }
    try { await staffAction(u.USER_ID, action, pw); setMsg({ ok: true, text: action === 'password' ? '비밀번호를 바꿨습니다' : '바꿨습니다' }); load(); }
    catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); }
  };
  const input: React.CSSProperties = { ...inp, minWidth: 0 };
  const td: React.CSSProperties = { padding: '9px 8px', borderBottom: '1px solid #F2F4F6' };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 680 }}>
        <div style={head()}><b>직원 관리 — {compName}</b><button onClick={onClose} style={xBtn}>✕</button></div>
        <form onSubmit={add} style={{ display: 'flex', gap: 6, padding: '4px 24px 12px', flexWrap: 'wrap' }}>
          <input placeholder="아이디 (영문·숫자 4~20자)" value={f.id} onChange={e => setF({ ...f, id: e.target.value })} style={{ ...input, flex: 2 }} autoComplete="off" />
          <input placeholder="이름" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={{ ...input, flex: 1 }} />
          <input placeholder="이메일 (선택 · 비밀번호 찾기용)" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} style={{ ...input, flex: 2 }} />
          <input placeholder="비밀번호 8자 이상" type="password" value={f.pw} onChange={e => setF({ ...f, pw: e.target.value })} style={{ ...input, flex: 2 }} autoComplete="new-password" />
          <select value={f.role} onChange={e => setF({ ...f, role: e.target.value as 'STAFF' | 'PROD' })} style={{ ...input, flex: 1 }}
            title="생산 직원: 생산 화면만 보임 (금액·견적·거래처 안 보임)">
            <option value="STAFF">직원</option>
            <option value="PROD">생산 직원</option>
          </select>
          <button type="submit" style={btn()}>직원 추가</button>
        </form>
        <div style={{ fontSize: 12, color: '#8B95A1', padding: '0 24px 8px' }}>생산 직원은 로그인하면 생산 화면만 보입니다 — 금액·견적서·거래처는 보이지 않습니다.</div>
        {msg && <div style={{ fontSize: 13, padding: '0 24px 8px', color: msg.ok ? '#03B26C' : '#F04452' }}>{msg.ok ? '✓' : '⚠'} {msg.text}</div>}
        <div style={{ overflow: 'auto', padding: '0 24px 20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['아이디', '이름', '권한', '상태', '마지막 로그인', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ddd' }}>{h}</th>)}
            </tr></thead>
            <tbody>{(rows ?? []).map(u => (
              <tr key={u.USER_ID} style={{ opacity: u.USE_YN === 'Y' ? 1 : 0.5 }}>
                <td style={td}>{u.USER_ID}{u.USER_ID === me && ' (나)'}</td>
                <td style={td}>{u.USER_NM}</td>
                <td style={td}>{ROLE[u.ROLE] ?? u.ROLE}</td>
                <td style={td}>{u.USE_YN === 'Y' ? '사용' : '중지'}</td>
                <td style={{ ...td, color: '#666' }}>{fmt(u.LAST_LOGIN_DT) || '-'}</td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {(u.ROLE === 'STAFF' || u.ROLE === 'PROD') && <>
                    <button style={sm('#5C6BC0')} onClick={() => act(u, 'password')}>비밀번호</button>
                    {u.USE_YN === 'Y' ? <button style={sm('#616161')} onClick={() => act(u, 'disable')}>중지</button>
                      : <button style={sm('#03B26C')} onClick={() => act(u, 'enable')}>다시 사용</button>}
                  </>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/** 1:1 문의함 (최고관리자) — 답변하면 문의한 사람 메일로 감 */
export const InquiryDialog: React.FC<{ onClose: () => void; onChanged?: () => void }> = ({ onClose, onChanged }) => {
  const [tab, setTab] = useState<'NEW' | 'DONE' | ''>('NEW');
  const [rows, setRows] = useState<InquiryRow[] | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [reply, setReply] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const load = (t = tab) => listInquiries(t).then(r => setRows(r.data)).catch(e => setMsg({ ok: false, text: errMsg(e) }));
  useEffect(() => { setRows(null); load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (q: InquiryRow, withReply: boolean) => {
    if (withReply && !reply.trim()) { setMsg({ ok: false, text: '답변 내용을 쓰세요' }); return; }
    try {
      const r = await replyInquiry(q.INQ_ID, withReply ? reply.trim() : '');
      setMsg({ ok: true, text: withReply ? `답변 메일을 보냈습니다 (${r.data.sent})` : '처리 완료로 표시했습니다' });
      setReply(''); setOpen(null); load(); onChanged?.();
    } catch (e) { setMsg({ ok: false, text: errMsg(e) }); }
  };
  return (
    <div style={overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...box, maxWidth: 820 }}>
        <div style={head()}><b>1:1 문의함</b><button onClick={onClose} style={xBtn}>✕</button></div>
        <div style={{ display: 'flex', gap: 6, padding: '4px 24px 12px', flexWrap: 'wrap' }}>
          {([['NEW', '새 문의'], ['DONE', '답변 완료'], ['', '전체']] as const).map(([k, l]) => (
            <button key={k || 'all'} onClick={() => setTab(k)} style={chip(tab === k)}>{l}</button>
          ))}
        </div>
        {msg && <div style={{ fontSize: 13, padding: '0 24px 8px', color: msg.ok ? '#03B26C' : '#F04452' }}>{msg.ok ? '✓' : '⚠'} {msg.text}</div>}
        <div style={{ overflow: 'auto', padding: '0 24px 20px' }}>
          {rows === null ? <div style={{ color: '#888', padding: 12 }}>불러오는 중…</div>
            : rows.length === 0 ? <div style={{ color: '#888', padding: 12 }}>{tab === 'NEW' ? '새 문의가 없습니다' : '없습니다'}</div>
            : rows.map(q => (
              <div key={q.INQ_ID} style={{ border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', flexWrap: 'wrap' }} onClick={() => { setOpen(open === q.INQ_ID ? null : q.INQ_ID); setReply(''); }}>
                  <b style={{ color: q.STATUS === 'NEW' ? '#F76707' : '#03B26C' }}>{q.STATUS === 'NEW' ? '새 문의' : '답변 완료'}</b>
                  <b>{q.NAME}</b><span>{q.COMPANY || ''}</span>
                  <span style={{ color: '#666' }}>{q.TEL || ''} {q.EMAIL || ''}</span>
                  <span style={{ marginLeft: 'auto', color: '#999' }}>{fmt(q.REG_DT)}</span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', margin: '6px 0', color: '#333', lineHeight: 1.6 }}>{open === q.INQ_ID ? q.CONTENT : q.CONTENT.slice(0, 90) + (q.CONTENT.length > 90 ? '…' : '')}</div>
                {q.REPLY && <div style={{ whiteSpace: 'pre-wrap', background: '#F1F8E9', borderRadius: 6, padding: 8, color: '#33691E' }}>↳ {q.REPLY} <span style={{ color: '#999' }}>({fmt(q.REPLY_DT)} {q.REPLY_BY})</span></div>}
                {open === q.INQ_ID && q.STATUS === 'NEW' && (
                  <div style={{ marginTop: 8 }}>
                    <textarea value={reply} onChange={e => setReply(e.target.value)} rows={4} placeholder={q.EMAIL ? `${q.EMAIL} 로 보낼 답변` : '이메일이 없는 문의 — 전화로 답하고 [처리 완료]'}
                      disabled={!q.EMAIL} style={{ width: '100%', padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                      <button style={sm('#8B95A1')} onClick={() => send(q, false)}>처리 완료 (답변 없이)</button>
                      {q.EMAIL && <button style={sm('#3182F6')} onClick={() => send(q, true)}>답변 메일 보내기</button>}
                    </div>
                  </div>
                )}
                {q.NOTI_MSG && open === q.INQ_ID && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>관리자 알림: {q.NOTI_MSG}</div>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
